import React from "react";

const BASE_API = "https://scanmymenu-nm38.onrender.com/api/restaurants";

const PLANS = [
  {
    id: "basic",
    name: "Basic",
    emoji: "🍽️",
    tagline: "Get started with digital menus",
    price: 9,
    renewPrice: 149,
    color: "#2563eb",
    gradient: "linear-gradient(135deg,#2563eb,#60a5fa)",
    glow: "rgba(37,99,235,0.18)",
    border: "#bfdbfe",
    bg: "#eff6ff",
    features: [
      "Up to 50 Menu Items",
      "Basic QR Code Access",
      "Standard Menu Display",
      "Edit Menu Anytime",
      "Email Support",
    ],
    cta: "Start with Basic",
    badge: null,
  },
  {
    id: "pro",
    name: "Pro",
    emoji: "⚡",
    tagline: "Power up your restaurant operations",
    price: 9,
    renewPrice: 499,
    color: "#ff6b00",
    gradient: "linear-gradient(135deg,#ff6b00,#ff914d)",
    glow: "rgba(255,107,0,0.18)",
    border: "#ffe8d9",
    bg: "#fff7f2",
    features: [
      "Unlimited Menu Items",
      "Online Ordering System",
      "Clean Analytics Dashboard",
      "Smart QR Access",
      "Priority Customer Support",
      "Custom Menu Categories",
      "Real-time Order Tracking",
      "Table Management System",
    ],
    cta: "Upgrade to Pro",
    badge: "MOST POPULAR",
    featured: true,
  },
  {
    id: "max",
    name: "Max",
    emoji: "🚀",
    tagline: "The ultimate restaurant experience",
    price: 9,
    renewPrice: 999,
    color: "#7c3aed",
    gradient: "linear-gradient(135deg,#7c3aed,#a78bfa)",
    glow: "rgba(124,58,237,0.18)",
    border: "#ede9fe",
    bg: "#f5f3ff",
    features: [
      "Everything in Pro",
      "Custom Website Color Themes",
      "3D Food Item Models",
      "AR Menu Visualization",
      "AI-Powered Recommendations",
      "Multi-Branch Management",
      "White-label Branding",
      "Dedicated Account Manager",
      "Advanced SEO Tools",
      "Customer Loyalty Program",
    ],
    cta: "Go Maximum",
    badge: "PREMIUM",
  },
];

/* ── responsive hook ── */
function useWindowSize() {
  const [size, setSize] = React.useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
  });
  React.useEffect(() => {
    const handler = () => setSize({ width: window.innerWidth });
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return size;
}

export default function OwnerUpgrade() {
  const { width } = useWindowSize();

  const isMobile  = width < 480;
  const isTablet  = width >= 480 && width < 1024;
  const isDesktop = width >= 1024;

  const [activePlan, setActivePlan]           = React.useState(null);
  const [subscribedPlans, setSubscribedPlans] = React.useState({});
  const [loading, setLoading]                 = React.useState(true);
  const [paymentLoading, setPaymentLoading]   = React.useState(null);

  const restaurantId = localStorage.getItem("restaurantId");

  React.useEffect(() => {
    fetch(`${BASE_API}/restaurant/${restaurantId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.restaurant?.subscriptionStatus === "active") {
          const plan = data.restaurant?.plan || "basic";
          setActivePlan(plan);
          setSubscribedPlans({ [plan]: true });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [restaurantId]);

  const loadRazorpay = () =>
    new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload  = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handleUpgrade = async (plan) => {
    setPaymentLoading(plan.id);
    const loaded = await loadRazorpay();
    if (!loaded) {
      setPaymentLoading(null);
      alert("Razorpay failed to load");
      return;
    }
    try {
      const subRes  = await fetch(`${BASE_API}/payment/create-subscription`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ restaurantId, plan: plan.id }),
      });
      const subData = await subRes.json();
      if (!subData.success) {
        setPaymentLoading(null);
        alert("Subscription creation failed");
        return;
      }
      const options = {
        key:             "rzp_live_SoVS3SKtwY9HhY",
        subscription_id: subData.subscription.id,
        name:            "ScanMyMenu",
        description:     `₹9 First Month then ₹${plan.renewPrice}/month`,
        image:           "/logo.png",
        recurring:       true,
        theme:           { color: plan.color },
        prefill:         { name: "", email: "", contact: "" },
        notes:           { plan: `${plan.name} Plan` },
        handler: async function (response) {
          try {
            await fetch(`${BASE_API}/payment/verify-subscription`, {
              method:  "POST",
              headers: { "Content-Type": "application/json" },
              body:    JSON.stringify({
                razorpay_payment_id:      response.razorpay_payment_id,
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_signature:       response.razorpay_signature,
                restaurantId,
                plan: plan.id,
              }),
            });
          } catch (e) { console.log(e); }
          setActivePlan(plan.id);
          setSubscribedPlans({ [plan.id]: true });
          alert(`${plan.name} Plan Activated 🚀`);
        },
        modal: { ondismiss: () => setPaymentLoading(null) },
      };
      const razorpay = new window.Razorpay(options);
      setPaymentLoading(null);
      razorpay.open();
    } catch (error) {
      console.log(error);
      alert("Something went wrong");
      setPaymentLoading(null);
    }
  };

  /* ── responsive value map ── */
  const rv = {
    pagePadding:   isMobile ? "20px 12px 36px" : isTablet ? "28px 20px 40px" : "44px 40px 60px",
    titleSize:     isMobile ? "22px" : isTablet ? "28px" : "36px",
    subtitleSize:  isMobile ? "13px" : "15px",
    badgeFontSize: isMobile ? "10px" : "11px",
    headerMB:      isMobile ? "24px" : isTablet ? "28px" : "40px",
    gridStyle: isDesktop
      ? { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "24px", maxWidth: "1100px", margin: "0 auto", alignItems: "start" }
      : { display: "flex", flexDirection: "column", gap: isMobile ? "16px" : "20px", maxWidth: isTablet ? "500px" : "100%", margin: "0 auto" },
    cardPadding:   isMobile ? "16px 12px" : isTablet ? "22px 18px" : "28px 24px",
    cardRadius:    isMobile ? "18px" : "26px",
    featuredExtra: isDesktop ? { marginTop: "-14px", marginBottom: "-14px" } : { transform: "scale(1.02)" },
    iconSize:      isMobile ? "44px" : "54px",
    iconRadius:    isMobile ? "12px" : "16px",
    emojiSize:     isMobile ? "20px" : "26px",
    iconGap:       isMobile ? "10px" : "14px",
    headerMBCard:  isMobile ? "12px" : "18px",
    nameSize:      isMobile ? "18px" : isTablet ? "20px" : "22px",
    taglineSize:   isMobile ? "11px" : "13px",
    pricePad:      isMobile ? "10px 12px" : "16px",
    priceRadius:   isMobile ? "12px" : "18px",
    priceMB:       isMobile ? "14px" : "20px",
    bigPriceSize:  isMobile ? "40px" : isTablet ? "46px" : "52px",
    firstMonthSize:isMobile ? "12px" : "14px",
    oldPriceSize:  isMobile ? "12px" : "14px",
    renewSize:     isMobile ? "11px" : "12px",
    featGap:       isMobile ? "7px" : "10px",
    featPad:       isMobile ? "7px 10px" : "10px 14px",
    featRadius:    isMobile ? "10px" : "12px",
    featTextSize:  isMobile ? "12px" : "14px",
    checkSize:     isMobile ? "13px" : "16px",
    featMB:        isMobile ? "14px" : "22px",
    ctaPad:        isMobile ? "13px" : "16px",
    ctaFontSize:   isMobile ? "14px" : "16px",
    ctaRadius:     isMobile ? "12px" : "16px",
    footerSize:    isMobile ? "11px" : "13px",
    footerMT:      isMobile ? "20px" : "30px",
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg,#fafafa,#ffffff)", padding: rv.pagePadding, fontFamily: "'Segoe UI', sans-serif", boxSizing: "border-box" }}>

      {/* HEADER */}
      <div style={{ textAlign: "center", marginBottom: rv.headerMB }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg,#ff6b00,#ff914d)", color: "#fff", fontSize: rv.badgeFontSize, fontWeight: "700", letterSpacing: "1.5px", padding: isMobile ? "5px 14px" : "6px 18px", borderRadius: "20px", marginBottom: "12px" }}>
          🔥 LIMITED TIME OFFER
        </div>
        <h1 style={{ fontSize: rv.titleSize, fontWeight: "800", color: "#111", margin: "0 0 10px", lineHeight: "1.2" }}>
          Choose Your Plan
        </h1>
        <p style={{ fontSize: rv.subtitleSize, color: "#666", margin: 0 }}>
          All plans start at just <strong>₹9</strong> for the first month
        </p>
      </div>

      {/* PLAN CARDS */}
      <div style={rv.gridStyle}>
        {PLANS.map((plan) => {
          const isActive  = subscribedPlans[plan.id];
          const isLoading = paymentLoading === plan.id;

          return (
            <div
              key={plan.id}
              style={{
                borderRadius: rv.cardRadius,
                padding:      rv.cardPadding,
                position:     "relative",
                overflow:     "hidden",
                transition:   "box-shadow 0.3s ease, transform 0.3s ease",
                border:       plan.featured ? `2px solid ${plan.color}` : `1px solid ${plan.border}`,
                background:   plan.featured ? "#fff" : plan.bg,
                boxShadow:    plan.featured ? `0 20px 50px ${plan.glow}` : "0 4px 16px rgba(0,0,0,0.05)",
                ...(plan.featured ? rv.featuredExtra : {}),
              }}
            >
              {/* RIBBON */}
              {plan.badge && (
                <div style={{ position: "absolute", top: isMobile ? "13px" : "16px", right: isMobile ? "-46px" : "-52px", background: plan.gradient, color: "#fff", fontSize: isMobile ? "8px" : "9px", fontWeight: "800", letterSpacing: "1.5px", padding: isMobile ? "6px 52px" : "7px 60px", transform: "rotate(45deg)" }}>
                  {plan.badge}
                </div>
              )}

              {/* PLAN HEADER */}
              <div style={{ display: "flex", alignItems: "center", gap: rv.iconGap, marginBottom: rv.headerMBCard }}>
                <div style={{ width: rv.iconSize, height: rv.iconSize, minWidth: rv.iconSize, borderRadius: rv.iconRadius, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: plan.bg, border: `1px solid ${plan.border}` }}>
                  <span style={{ fontSize: rv.emojiSize }}>{plan.emoji}</span>
                </div>
                <div style={{ minWidth: 0 }}>
                  <h2 style={{ fontSize: rv.nameSize, fontWeight: "800", color: plan.color, margin: "0 0 2px", whiteSpace: "nowrap" }}>
                    {plan.name}
                  </h2>
                  <p style={{ fontSize: rv.taglineSize, color: "#777", margin: 0, whiteSpace: isMobile ? "normal" : "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {plan.tagline}
                  </p>
                </div>
              </div>

              {/* PRICE BOX */}
              <div style={{ borderRadius: rv.priceRadius, padding: rv.pricePad, textAlign: "center", marginBottom: rv.priceMB, background: plan.bg, border: `1px solid ${plan.border}` }}>
                <p style={{ textDecoration: "line-through", color: "#aaa", fontSize: rv.oldPriceSize, margin: "0 0 4px" }}>
                  ₹{plan.renewPrice}/month
                </p>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-end", gap: "8px" }}>
                  <span style={{ fontSize: rv.bigPriceSize, fontWeight: "800", lineHeight: "1", color: plan.color }}>
                    ₹9
                  </span>
                  <span style={{ fontSize: rv.firstMonthSize, fontWeight: "700", color: "#444", marginBottom: "6px" }}>
                    First Month
                  </span>
                </div>
                <p style={{ fontSize: rv.renewSize, color: "#888", margin: "6px 0 0" }}>
                  Then ₹{plan.renewPrice}/month auto-renewal
                </p>
              </div>

              {/* FEATURES */}
              <div style={{ display: "flex", flexDirection: "column", gap: rv.featGap, marginBottom: rv.featMB }}>
                {plan.features.map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", background: "rgba(255,255,255,0.7)", padding: rv.featPad, borderRadius: rv.featRadius, border: "1px solid rgba(0,0,0,0.04)" }}>
                    <span style={{ fontWeight: "700", fontSize: rv.checkSize, flexShrink: 0, marginTop: "1px", color: plan.color }}>✓</span>
                    <span style={{ fontSize: rv.featTextSize, fontWeight: "600", color: "#333", lineHeight: "1.4" }}>{f}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              {loading ? (
                <button style={{ width: "100%", border: "none", color: "#fff", padding: rv.ctaPad, borderRadius: rv.ctaRadius, fontSize: rv.ctaFontSize, fontWeight: "700", background: plan.gradient, opacity: 0.6, cursor: "default" }}>
                  Loading...
                </button>
              ) : isActive ? (
                <div style={{ borderRadius: rv.ctaRadius, padding: rv.ctaPad, textAlign: "center", fontWeight: "700", fontSize: rv.ctaFontSize, background: plan.bg, border: `1px solid ${plan.border}`, color: plan.color }}>
                  ✅ Active Plan
                  <p style={{ marginTop: "6px", fontSize: isMobile ? "11px" : "13px", opacity: 0.7, fontWeight: "500" }}>
                    You're on {plan.name}
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => handleUpgrade(plan)}
                  disabled={isLoading || !!activePlan}
                  style={{ width: "100%", border: "none", color: "#fff", padding: rv.ctaPad, borderRadius: rv.ctaRadius, fontSize: rv.ctaFontSize, fontWeight: "700", transition: "0.3s ease", background: isLoading || activePlan ? "#ccc" : plan.gradient, cursor: isLoading || activePlan ? "not-allowed" : "pointer", opacity: activePlan && !isActive ? 0.6 : 1, boxShadow: `0 8px 20px ${plan.glow}` }}
                >
                  {isLoading ? "Opening Payment..." : plan.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* FOOTER */}
      <p style={{ textAlign: "center", color: "#aaa", fontSize: rv.footerSize, marginTop: rv.footerMT }}>
        🔒 Secure payments by Razorpay · Cancel anytime
      </p>
    </div>
  );
}